/**
 * Where a parent proposes changes to their household record.
 *
 * Deliberately not a save dialog: nothing here writes to the family. The parent
 * describes what should be true, the school decides. So the primary button says
 * "Send to school", and the copy is about asking rather than editing.
 *
 * It opens on a single section, because that is how it is reached: a pencil on the
 * card the parent was looking at. Asking about an address should not present the
 * whole record. The request carries only the parts this guardian changed from how
 * the dialog opened. The school reads a part sent back as the record has it as
 * "put it back", so resending an untouched one would erase a change another
 * guardian sent after this page loaded.
 */
import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Card, CardContent } from '~/components/ui/card';
import type { HouseholdSection } from '../MyFamily/ParentFamilyInfo';
import { useSubmitHouseholdChangeRequest } from './useSubmitHouseholdChangeRequest';

const SECTION_TITLES: Record<HouseholdSection, string> = {
  guardians: 'Request a change to parents',
  emergency: 'Request a change to emergency contacts',
  pickup: 'Request a change to authorized pickup',
  address: 'Request a change to your address',
  church: 'Request a change to church affiliation',
};

interface Contact {
  name: string;
  phone: string;
  relationship: string;
}

export interface GuardianRow {
  person: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
    hasUserAccount?: boolean | null;
  };
}

export interface HouseholdFamily {
  id: string;
  guardians?: GuardianRow[] | null;
  emergencyContacts?: Array<{ name: string; phone?: string | null; relationship?: string | null }> | null;
  authorizedPickup?: Array<{ name: string; phone?: string | null; relationship?: string | null }> | null;
  churchAffiliation?: { churchName?: string | null; pastorName?: string | null; churchEmail?: string | null } | null;
  address?: {
    street?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  } | null;
}

interface EditHouseholdDialogProps {
  /** Which card's pencil was clicked; the dialog shows only that section. */
  section: HouseholdSection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  family: HouseholdFamily;
  onSubmitted?: () => void;
}

function ContactList({
  contacts,
  onChange,
  label,
}: {
  contacts: Contact[];
  onChange: (contacts: Contact[]) => void;
  label: string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...contacts, { name: '', phone: '', relationship: '' }])}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>

      {contacts.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">No {label.toLowerCase()} yet.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact, index) => (
            <Card key={index}>
              <CardContent className="pb-3 pt-4">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    aria-label={`${label} ${index + 1} name`}
                    placeholder="Name"
                    value={contact.name}
                    onChange={(e) => {
                      const next = [...contacts];
                      next[index] = { ...next[index], name: e.target.value };
                      onChange(next);
                    }}
                  />
                  <Input
                    aria-label={`${label} ${index + 1} phone`}
                    placeholder="Phone"
                    value={contact.phone}
                    onChange={(e) => {
                      const next = [...contacts];
                      next[index] = { ...next[index], phone: e.target.value };
                      onChange(next);
                    }}
                  />
                  <div className="flex gap-2">
                    <Input
                      aria-label={`${label} ${index + 1} relationship`}
                      placeholder="Relationship"
                      value={contact.relationship}
                      onChange={(e) => {
                        const next = [...contacts];
                        next[index] = { ...next[index], relationship: e.target.value };
                        onChange(next);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${label} ${index + 1}`}
                      className="shrink-0 text-destructive hover:text-destructive"
                      onClick={() => onChange(contacts.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const toContacts = (rows: HouseholdFamily['emergencyContacts']): Contact[] =>
  (rows ?? []).map((row) => ({
    name: row.name ?? '',
    phone: row.phone ?? '',
    relationship: row.relationship ?? '',
  }));

const cleanContacts = (contacts: Contact[]) =>
  contacts
    .filter((contact) => contact.name.trim())
    .map((contact) => ({
      name: contact.name.trim(),
      phone: contact.phone.trim() || null,
      relationship: contact.relationship.trim() || null,
    }));

type HouseholdForm = {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  emergencyContacts: Contact[];
  authorizedPickup: Contact[];
  churchName: string;
  pastorName: string;
  churchEmail: string;
};

type GuardianEdits = Record<string, { phone: string; email: string }>;

const EMPTY_FORM: HouseholdForm = {
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  emergencyContacts: [],
  authorizedPickup: [],
  churchName: '',
  pastorName: '',
  churchEmail: '',
};

const formFrom = (family: HouseholdFamily): HouseholdForm => ({
  street: family.address?.street ?? '',
  street2: family.address?.street2 ?? '',
  city: family.address?.city ?? '',
  state: family.address?.state ?? '',
  zip: family.address?.zip ?? '',
  country: family.address?.country ?? '',
  emergencyContacts: toContacts(family.emergencyContacts),
  authorizedPickup: toContacts(family.authorizedPickup),
  churchName: family.churchAffiliation?.churchName ?? '',
  pastorName: family.churchAffiliation?.pastorName ?? '',
  churchEmail: family.churchAffiliation?.churchEmail ?? '',
});

const guardianEditsFrom = (guardians: GuardianRow[]): GuardianEdits =>
  Object.fromEntries(guardians.map((g) => [g.person.id, { phone: g.person.phone ?? '', email: g.person.email ?? '' }]));

/** Each household part as the request sends it. */
const blocksFrom = (form: HouseholdForm) => ({
  address: {
    street: form.street.trim() || null,
    street2: form.street2.trim() || null,
    city: form.city.trim() || null,
    state: form.state.trim() || null,
    zip: form.zip.trim() || null,
    country: form.country.trim() || null,
  },
  emergencyContacts: cleanContacts(form.emergencyContacts),
  authorizedPickup: cleanContacts(form.authorizedPickup),
  churchAffiliation: {
    churchName: form.churchName.trim() || null,
    pastorName: form.pastorName.trim() || null,
    churchEmail: form.churchEmail.trim() || null,
  },
});

const guardianBlock = (edits: GuardianEdits, personId: string) => ({
  personId,
  phone: edits[personId]?.phone?.trim() || null,
  email: edits[personId]?.email?.trim() || null,
});

const differs = (now: unknown, then: unknown) => JSON.stringify(now) !== JSON.stringify(then);

export function EditHouseholdDialog({ section, open, onOpenChange, family, onSubmitted }: EditHouseholdDialogProps) {
  const guardians = family.guardians ?? [];
  const [form, setForm] = useState<HouseholdForm>(EMPTY_FORM);
  const [guardianEdits, setGuardianEdits] = useState<GuardianEdits>({});
  // What the dialog opened with: only the parts that differ from it are sent.
  const [opened, setOpened] = useState<{ form: HouseholdForm; guardianEdits: GuardianEdits }>({
    form: EMPTY_FORM,
    guardianEdits: {},
  });
  const [errors, setErrors] = useState<string[]>([]);

  const [submitChangeRequest, { loading }] = useSubmitHouseholdChangeRequest();

  useEffect(() => {
    if (!open) return;
    setErrors([]);
    const openedForm = formFrom(family);
    const openedGuardianEdits = guardianEditsFrom(guardians);
    setForm(openedForm);
    setGuardianEdits(openedGuardianEdits);
    setOpened({ form: openedForm, guardianEdits: openedGuardianEdits });
    // guardians is derived from family; family identity is what should reset the form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family, open]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrors([]);

    const now = blocksFrom(form);
    const then = blocksFrom(opened.form);
    const changedGuardians = guardians
      .map((g) => g.person.id)
      .filter((personId) => differs(guardianBlock(guardianEdits, personId), guardianBlock(opened.guardianEdits, personId)))
      .map((personId) => guardianBlock(guardianEdits, personId));

    const changes = {
      ...(differs(now.address, then.address) ? { address: now.address } : {}),
      ...(differs(now.emergencyContacts, then.emergencyContacts) ? { emergencyContacts: now.emergencyContacts } : {}),
      ...(differs(now.authorizedPickup, then.authorizedPickup) ? { authorizedPickup: now.authorizedPickup } : {}),
      ...(differs(now.churchAffiliation, then.churchAffiliation) ? { churchAffiliation: now.churchAffiliation } : {}),
      ...(changedGuardians.length > 0 ? { guardians: changedGuardians } : {}),
    };
    // Nothing to ask the school: close, as there's nothing to send.
    if (Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }

    void submitChangeRequest({
      variables: { familyId: family.id, ...changes },
      onCompleted: (result) => {
        const userErrors = result?.submitHouseholdChangeRequest?.userErrors;
        if (userErrors?.length) {
          setErrors(userErrors.map((e) => e.message));
          return;
        }
        onSubmitted?.();
        onOpenChange(false);
      },
      onError: (error) => setErrors([error.message]),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{SECTION_TITLES[section]}</DialogTitle>
          <DialogDescription>
            Tell the school what should be different. Nothing changes until someone at the school reviews it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {section === 'address' && (
            <div className="mt-2 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="street">Street address</Label>
                <Input
                  id="street"
                  value={form.street}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="street2">Apt/Suite (optional)</Label>
                <Input
                  id="street2"
                  value={form.street2}
                  onChange={(e) => setForm({ ...form, street2: e.target.value })}
                  placeholder="Apt 4B"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input id="state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP code</Label>
                  <Input id="zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country (optional)</Label>
                  <Input
                    id="country"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {section === 'guardians' && (
            <div className="mt-2 space-y-4">
              {guardians.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">No parents or guardians on record.</p>
              ) : (
                guardians.map(({ person }) => {
                  const edits = guardianEdits[person.id] ?? { phone: '', email: '' };
                  const emailLocked = Boolean(person.hasUserAccount);
                  return (
                    <Card key={person.id}>
                      <CardContent className="space-y-3 pb-4 pt-4">
                        <p className="font-medium">
                          {person.firstName} {person.lastName}
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor={`phone-${person.id}`}>Phone</Label>
                            <Input
                              id={`phone-${person.id}`}
                              value={edits.phone}
                              onChange={(e) =>
                                setGuardianEdits({
                                  ...guardianEdits,
                                  [person.id]: { ...edits, phone: e.target.value },
                                })
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`email-${person.id}`}>Email</Label>
                            <Input
                              id={`email-${person.id}`}
                              type="email"
                              value={edits.email}
                              disabled={emailLocked}
                              onChange={(e) =>
                                setGuardianEdits({
                                  ...guardianEdits,
                                  [person.id]: { ...edits, email: e.target.value },
                                })
                              }
                            />
                            {emailLocked && (
                              <p className="text-xs text-muted-foreground">
                                This email is the sign-in for this account, so it can&apos;t be changed here. Ask the
                                school office if it needs to move.
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
              <p className="text-xs text-muted-foreground">
                To change a name, or to add or remove a parent, contact the school office.
              </p>
            </div>
          )}

          {section === 'emergency' && (
            <div className="mt-2">
              <ContactList
                contacts={form.emergencyContacts}
                onChange={(emergencyContacts) => setForm({ ...form, emergencyContacts })}
                label="Emergency contacts"
              />
            </div>
          )}

          {section === 'pickup' && (
            <div className="mt-2">
              <ContactList
                contacts={form.authorizedPickup}
                onChange={(authorizedPickup) => setForm({ ...form, authorizedPickup })}
                label="Authorized pickup"
              />
            </div>
          )}

          {section === 'church' && (
            <div className="mt-2 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="churchName">Church name</Label>
                <Input
                  id="churchName"
                  value={form.churchName}
                  onChange={(e) => setForm({ ...form, churchName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pastorName">Pastor name</Label>
                <Input
                  id="pastorName"
                  value={form.pastorName}
                  onChange={(e) => setForm({ ...form, pastorName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="churchEmail">Church email</Label>
                <Input
                  id="churchEmail"
                  type="email"
                  value={form.churchEmail}
                  onChange={(e) => setForm({ ...form, churchEmail: e.target.value })}
                />
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div role="alert" className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {errors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send to school'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditHouseholdDialog;
